import { Injectable } from '@angular/core';
import { LoadingController, ToastController, AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';

/**
 * Helper service for managing UI in application
 */
@Injectable()
export class HelperProvider {
  private loading: HTMLIonLoadingElement;
  /** Distinguishes overlays across a hide/show cycle that overlaps creation. */
  private loadingGeneration = 0;
  public isLoading = false;

  constructor(
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private alertController: AlertController,
  ) { }

  /**
   * check if app is running on native platform
   */
  public get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * show native loading
   */
  async showLoading(text?: string): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    const generation = ++this.loadingGeneration;
    const loading = await this.loadingCtrl.create({
      ...(text && { message: text }),
    });

    // hideLoading may have run while this overlay was still being created —
    // there was nothing for it to dismiss then, so dismiss it on arrival.
    // Presenting it instead strands a spinner nothing else can reach.
    if (!this.isLoading || generation !== this.loadingGeneration) {
      loading.dismiss();
      return;
    }

    this.loading = loading;
    this.loading.present();
  }

  /**
   * hide loading. Timeout needs for preventing error when loadingCtrl is null
   */
  hideLoading = () => setTimeout(() => {
    if (!this.isLoading) {
      return;
    }

    this.isLoading = false;
    this.loadingGeneration++;
    // Undefined while the overlay is still being created; showLoading dismisses
    // it when it arrives.
    this.loading?.dismiss();
  }, 100)

  public async showToast(text: string) {
    const toast = await this.toastCtrl.create({
      message: text,
      duration: 2000,
    });
    toast.present();
  }

  public async alert(msg: string, title?: string) {
    const alert = await this.alertController.create({
      header: title || 'Failure',
      message: msg,
      buttons: ['OK'],
    });

    return alert.present();
  }

  public async confirm(handler: () => void, message?: string, header?: string, cancel?: () => void, confirmText = 'Confirm') {
    const alert = await this.alertController.create({
      header: header ? header : 'Confirmation',
      message: message ? message : 'Are you sure?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          ...(cancel && { handler: () => cancel() }),
        }, {
          text: confirmText,
          handler: () => handler(),
        },
      ],
    });

    await alert.present();
  }
}
